import { useMutation, useQuery, useQueryClient } from "react-query";

import { SubscriptionDto } from "doxecure-commons/doxecureClientTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { buyCreditsPack, cancelTerminationSubscriptionRequest, getActiveSubscription, requestTerminatingSubscription, startSubscriptionNow } from "@services/corporations.service";

import { SubscriptionID } from "./hooksConstants";
import { useAuthContext } from "./useAuthContext";

export const useSubscription = () => {
    const { connectedUser, isAuthenticated } = useAuthContext();
    const queryClient = useQueryClient();

    const corporationApid = connectedUser?.corporations?.first()?.apid;

    const query = useQuery<Nullable<SubscriptionDto>>({
        queryKey: [SubscriptionID, corporationApid],
        queryFn: () => getActiveSubscription(corporationApid),
        enabled: isAuthenticated && $ok(corporationApid),
        staleTime: 5 * 60 * 1000, // 5 min
    });

    const invalidate = () => queryClient.invalidateQueries([SubscriptionID, corporationApid]);

    const buyExtraPack = useMutation({
        mutationFn: () => buyCreditsPack(corporationApid),
        onSuccess: invalidate
    });

    const cancelTerminationRequest = useMutation({
        mutationFn: () => cancelTerminationSubscriptionRequest(corporationApid),
        onSuccess: invalidate
    });

    const requestCancelSubscription = useMutation({
        mutationFn: () => requestTerminatingSubscription(corporationApid),
        onSuccess: invalidate,
    });

    const startSubscriptionImmediately = useMutation({
        mutationFn: () => startSubscriptionNow(corporationApid),
        onSuccess: invalidate,
        onError: () => {
            setTimeout(() => invalidate(), 2000);
        }
    });

    return {
        ...query,
        buyExtraPack,
        cancelTerminationRequest,
        requestCancelSubscription,
        startSubscriptionImmediately
    }
};